import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

export default function MapLegendManager({ propertyId, propertyName, onColorsUpdated }) {
    const [colors, setColors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        unit_type_name: "",
        color_hex: "#B3D9FF",
        description: "",
        is_active: true,
    });

    useEffect(() => {
        fetchColors();
    }, [propertyId]);

    const fetchColors = async () => {
        setLoading(true);
        try {
            const url = propertyId
                ? `${process.env.REACT_APP_API_URL}map-legend-colors/property/${propertyId}`
                : `${process.env.REACT_APP_API_URL}map-legend-colors`;
            const response = await axios.get(url);
            setColors(response.data);
        } catch (error) {
            console.error("Error fetching legend colors:", error);
            toast.error("Failed to load legend colors");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                ...formData,
                property_id: propertyId || null,
            };

            if (editingId) {
                await axios.put(`${process.env.REACT_APP_API_URL}map-legend-colors/${editingId}`, payload);
                toast.success("Legend color updated successfully");
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}map-legend-colors`, payload);
                toast.success("Legend color created successfully");
            }

            resetForm();
            fetchColors();
            
            // Notify parent component that colors were updated
            if (onColorsUpdated) {
                onColorsUpdated();
            }
        } catch (error) {
            console.error("Error saving legend color:", error);
            toast.error(error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join(", ") : "Failed to save legend color");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (color) => {
        setEditingId(color.id);
        setFormData({
            unit_type_name: color.unit_type_name,
            color_hex: color.color_hex,
            description: color.description || "",
            is_active: color.is_active,
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this legend color?")) {
            return;
        }

        setLoading(true);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}map-legend-colors/${id}`);
            toast.success("Legend color deleted successfully");
            fetchColors();
        } catch (error) {
            console.error("Error deleting legend color:", error);
            toast.error("Failed to delete legend color");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({
            unit_type_name: "",
            color_hex: "#B3D9FF",
            description: "",
            is_active: true,
        });
    };

    const moveColor = async (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= colors.length) return;

        const items = Array.from(colors);
        const [movedItem] = items.splice(index, 1);
        items.splice(newIndex, 0, movedItem);

        const updatedItems = items.map((item, idx) => ({
            ...item,
            sort_order: idx,
        }));

        setColors(updatedItems);

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}map-legend-colors/sort-order`, {
                colors: updatedItems.map((item) => ({
                    id: item.id,
                    sort_order: item.sort_order,
                })),
            });
            toast.success("Order updated");
        } catch (error) {
            console.error("Error updating sort order:", error);
            toast.error("Failed to update order");
            fetchColors();
        }
    };

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">
                Map Legend Colors {propertyName && `- ${propertyName}`}
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">{editingId ? "Edit" : "Add"} Legend Color</h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Type Name</label>
                            <input
                                type="text"
                                value={formData.unit_type_name}
                                onChange={(e) => setFormData({ ...formData, unit_type_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Bachelor, Studio"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                            <div className="flex gap-2">
                                <input
                                    type="color"
                                    value={formData.color_hex}
                                    onChange={(e) => setFormData({ ...formData, color_hex: e.target.value })}
                                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                                />
                                <input
                                    type="text"
                                    value={formData.color_hex}
                                    onChange={(e) => setFormData({ ...formData, color_hex: e.target.value })}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="#B3D9FF"
                                    pattern="^#[0-9A-Fa-f]{6}$"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                            <input
                                type="text"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Light Blue for Bachelor units"
                            />
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                                Active
                            </label>
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? "Saving..." : editingId ? "Update" : "Add"}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* Legend List Section */}
                <div>
                    <h3 className="text-lg font-semibold mb-4">Current Legend Colors</h3>
                    {loading && colors.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">Loading...</div>
                    ) : colors.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No legend colors defined yet</div>
                    ) : (
                        <div className="space-y-2">
                            {colors.map((color, index) => (
                                <div
                                    key={color.id}
                                    className={`flex items-center gap-3 p-3 bg-white border rounded-lg shadow-sm ${
                                        !color.is_active ? "opacity-50" : ""
                                    }`}
                                >
                                    {/* Reorder Buttons */}
                                    <div className="flex flex-col gap-1">
                                        <button
                                            onClick={() => moveColor(index, 'up')}
                                            disabled={index === 0}
                                            className="p-1 text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                                            title="Move up"
                                        >
                                            ▲
                                        </button>
                                        <button
                                            onClick={() => moveColor(index, 'down')}
                                            disabled={index === colors.length - 1}
                                            className="p-1 text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                                            title="Move down"
                                        >
                                            ▼
                                        </button>
                                    </div>
                                    
                                    <div className="flex-shrink-0">
                                        <div
                                            className="w-12 h-12 rounded border-2 border-gray-300"
                                            style={{ backgroundColor: color.color_hex }}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900">{color.unit_type_name}</div>
                                        <div className="text-sm text-gray-500">{color.color_hex}</div>
                                        {color.description && (
                                            <div className="text-xs text-gray-400 truncate">{color.description}</div>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEdit(color)}
                                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(color.id)}
                                            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">💡 How it works:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Add colors for each unit type that appears on your property map</li>
                    <li>• The unit type name must match exactly with the unit types in your database</li>
                    <li>• Use ▲▼ arrows to reorder how they appear in the legend</li>
                    <li>• When you update a color, all units/beds of that type will automatically update on the map</li>
                    <li>• Inactive colors won't be applied to the map but are preserved in the database</li>
                </ul>
            </div>
        </div>
    );
}
